import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, UserPlus } from 'lucide-react';
import AuthLayout from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';

const inputClass =
  'h-11 border-[rgba(6, 43, 26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212, 175, 55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';

export default function Signup() {
  const navigate = useNavigate();
  const { signUpStudent } = useAuth();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const validate = () => {
    if (!form.fullName.trim()) return 'Please enter your full name';
    if (!form.email.trim()) return 'Please enter your email';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email';
    if (form.password.length < 8) return 'Password must be at least 8 characters';
    if (form.password !== form.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    try {
      setSubmitting(true);
      await signUpStudent({
        email: form.email.trim(),
        password: form.password,
        fullName: form.fullName.trim(),
        phone: form.phone.trim(),
      });
      toast.success('Account created — welcome!');
      navigate('/student/dashboard', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail || err?.message || 'Failed to create account';
      if (err?.response?.status === 409) {
        toast.error('An account with this email already exists. Try signing in.');
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      subtitle="Student registration"
      title="Create your student account"
      footer={
        <span>
          Already have an account?{' '}
          <Link
            to="/login"
            data-testid="signup-login-link"
            className="text-[#062B1A] font-semibold hover:text-[#D4AF37] transition-colors"
          >
            Sign in
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-2">
          <Label htmlFor="fullName" className="text-sm font-medium text-[#062B1A]">
            Full name
          </Label>
          <Input
            id="fullName"
            type="text"
            autoComplete="name"
            value={form.fullName}
            onChange={update('fullName')}
            placeholder="Jane Doe"
            data-testid="signup-fullname-input"
            className={inputClass}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-[#062B1A]">
            Email address
          </Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={update('email')}
            placeholder="you@example.com"
            data-testid="signup-email-input"
            className={inputClass}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium text-[#062B1A]">
            Phone <span className="text-[#062B1A]/40 font-normal">(optional)</span>
          </Label>
          <Input
            id="phone"
            type="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={update('phone')}
            placeholder="+1 555 123 4567"
            data-testid="signup-phone-input"
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-[#062B1A]">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                value={form.password}
                onChange={update('password')}
                placeholder="Min. 8 characters"
                data-testid="signup-password-input"
                className={`${inputClass} pr-10`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#062B1A]/40 hover:text-[#062B1A] transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#062B1A]">
              Confirm password
            </Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              value={form.confirmPassword}
              onChange={update('confirmPassword')}
              placeholder="Repeat password"
              data-testid="signup-confirm-input"
              className={inputClass}
            />
          </div>
        </div>

        <div className="rounded-md border border-[rgba(212, 175, 55,0.35)] bg-[rgba(212, 175, 55,0.06)] px-4 py-3">
          <p className="text-xs text-[#062B1A]/70 leading-relaxed">
            You'll be signed in automatically and routed to your student dashboard.
          </p>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="signup-submit-button"
          className="w-full h-11 bg-[#062B1A] text-white hover:bg-[#041B10] focus-visible:ring-4 focus-visible:ring-[rgba(212, 175, 55,0.35)] font-medium"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating account…
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4 mr-2" />
              Create account
            </>
          )}
        </Button>

        <p className="text-[11px] text-[#062B1A]/50 text-center leading-relaxed">
          By creating an account you agree to our terms of use. Instructor accounts can only
          be created by an administrator.
        </p>
      </form>
    </AuthLayout>
  );
}
